<template>
  <div class="document-cpt-codes-tab-container">
    <div class="position-absolute w-100 h-100">
      <div class="overflow-auto w-100" style="height: calc(100% - 50px)">
        <b-form-group
          :state="cptCodesErrors.value < 0 ? true : false"
          :invalid-feedback="cptCodesErrors.join('. ')"
        >
          <div
            v-for="code in cptCodes"
            :key="code.service.id"
            class="d-flex flex-column mx-2 mb-4"
          >
            <div class="d-flex flex-row align-items-center">
              <div class="cpt-code-title">
                {{
                  code.service.billingCodes &&
                  code.service.billingCodes.length > 0
                    ? code.service.billingCodes[0].cptCode +
                      " " +
                      code.service.name
                    : code.service.name
                }}
              </div>
              <div
                v-if="!disableControls"
                class="ml-auto"
                v-html="dropdownEllipsis(code)"
              ></div>
            </div>
            <div class="my-1 cpt-code-content">
              <div class="d-flex align-items-center">
                <span
                  v-show="
                    hasCurrentUserPermissionAccess(
                      'others',
                      'patientBillingInfo'
                    )
                  "
                  style="width: 84px; font-size: 12px;"
                  class="ml-2"
                >
                  Cost/Unit ($)
                </span>
                <span style="width: 98px; font-size: 12px;" class="ml-2">
                  Number of units
                </span>
              </div>
            </div>
            <div class="my-1 cpt-code-content">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-show="
                    hasCurrentUserPermissionAccess(
                      'others',
                      'patientBillingInfo'
                    )
                  "
                  style="width: 80px;"
                  class="ml-2"
                  v-model="code.costPerUnit"
                  type="number"
                  size="sm"
                  id="costPerUnit"
                  @change="updateServices"
                  :disabled="disableControls"
                ></b-form-input>
                <b-form-input
                  style="width: 80px;"
                  class="ml-2"
                  v-model="code.numberOfUnits"
                  type="number"
                  size="sm"
                  id="numberOfUnits"
                  @change="updateServices"
                  :disabled="disableControls"
                ></b-form-input>
              </div>
            </div>
          </div>
        </b-form-group>
      </div>
      <div
        class="d-flex justify-content-center align-items-center"
        style="height: 50px"
      >
        <b-button
          variant="primary"
          @click="addCptCode"
          :disabled="disableControls"
        >
          Add CPT Code
        </b-button>
      </div>
    </div>
    <eoc-view-add-edit-diagnosis-codes-modal
      v-if="showCptCodeModal"
      v-model="showCptCodeModal"
      :diagnosisCodeId="diagnosisCodeId"
      :eocId="eocId"
      :patientId="patientId"
      @onModify="onDiagnosisCodeModify"
    />
    <b-modal
      centered
      v-model="editorShown"
      ref="form-modal"
      :title="'Add CPT Code'"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok.prevent="submitCtpCode"
      no-close-on-backdrop
      header-class="bg-purple"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-form-group
        label="CPT Codes"
        :state="cptCodesErrors.value < 0 ? true : false"
        :invalid-feedback="servicesErrors.join('. ')"
      >
        <multiple-select-unit-rate
          v-model="services"
          :options="serviceOptions"
          hideMods
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EocViewAddEditDiagnosisCodesModal from "../EocViewAddEditDiagnosisCodesModal.vue";
import scheduleEventServer from "../../../server/scheduleEvent";
import serviceServer from "../../../server/service";
import userServer from "../../../server/user";
import MultipleSelectUnitRate from "../../controls/MultipleSelectUnitRate.vue";
import {
  validateNumberNotNegative,
  validateNumberPositive
} from "../../../helpers/validation";

export default {
  name: "DocumentCptCodesTabContent",
  components: {
    EocViewAddEditDiagnosisCodesModal,
    MultipleSelectUnitRate
  },
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    scheduleEventId: {
      type: String
    }
  },
  data() {
    return {
      diagnosisCodes: [],
      showCptCodeModal: false,
      diagnosisCodeId: null,
      cptCodes: [],
      serviceOptions: [],
      services: [],
      encounterSubmited: false,
      cptCodesErrors: [],
      servicesErrors: [],
      scheduleEvent: null
    };
  },
  mounted() {
    this.getCptCodes();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    validateServices(services = null) {
      var withoutErrors = true;
      if (!services) {
        this.cptCodesErrors = [];
        for (let i = 0; i < this.cptCodes.length; i++) {
          const code = this.cptCodes[i];
          if (
            !validateNumberNotNegative(code.costPerUnit) ||
            !validateNumberPositive(code.numberOfUnits)
          ) {
            this.cptCodesErrors.push("This field must have a positive value");
            withoutErrors = false;
            break;
          }
        }
      } else {
        this.servicesErrors = [];
        for (let i = 0; i < services.length; i++) {
          const code = services[i];
          if (
            !validateNumberNotNegative(code.costPerUnit) ||
            !validateNumberPositive(code.numberOfUnits)
          ) {
            this.servicesErrors.push("This field must have a positive value");
            withoutErrors = false;
            break;
          }
        }
      }
      return withoutErrors;
    },
    async getCptCodes() {
      this.scheduleEvent = await scheduleEventServer.get(this.scheduleEventId);
      this.cptCodes = this.scheduleEvent.services;
      this.encounterSubmited = this.scheduleEvent.billingInfo?.encounterSubmited;
    },
    async fetchServices() {
      const services = await serviceServer.query({
        type: "Service"
      });
      this.serviceOptions = services.map(service => ({
        value: service.id,
        label: `${
          service.billingCodes && service.billingCodes.length > 0
            ? service.billingCodes[0].cptCode + " - " + service.name
            : service.name
        }`,
        data: service,
        costPerUnit: service.costPerUnit,
        numberOfUnits: 1,
        billingCodes: service.billingCodes.map(bc => ({
          ...bc,
          mod1: bc.mod1 ? bc.mod1 : "",
          mod2: bc.mod2 ? bc.mod2 : "",
          mod3: bc.mod3 ? bc.mod3 : "",
          mod4: bc.mod4 ? bc.mod4 : ""
        }))
      }));
      this.serviceOptions = this.serviceOptions.filter(
        x => !this.cptCodes.some(y => y.service.id == x.value)
      );
      if (this.scheduleEvent) {
        if (this.scheduleEvent.users[0]) {
          const users = await userServer.getUserServices();
          const providers = users.filter(user => user.providerInfo);
          const provider = providers.find(
            user => user.id === this.scheduleEvent.users[0].id
          );
          this.serviceOptions = provider
            ? this.serviceOptions.filter(s =>
                provider.services?.includes(s.value)
              )
            : this.serviceOptions;
        }
      }
    },
    dropdownEllipsis(cptCode) {
      const dropdown = this.createDropdownButton({
        items: [
          {
            content: "Delete",
            callback: () => {
              this.$dialog
                .alert(
                  {
                    title: "Are you sure you want to delete this record?"
                  },
                  {
                    view: "confirm-delete"
                  }
                )
                .then(async () => {
                  this.cptCodes = this.cptCodes.filter(
                    x => x.service.id != cptCode.service.id
                  );

                  await this.updateServices()
                    .then(() => this.showAlertSuccess())
                    .catch(() => this.showError());

                  this.getCptCodes();
                });
            }
          }
        ]
      });
      this.hookDropdownButtons();

      return dropdown;
    },
    async addCptCode() {
      this.services = [];
      await this.fetchServices();
      this.$refs["form-modal"].show();
    },
    async submitCtpCode() {
      const currentServices = this.cptCodes.map(code => ({
        serviceId: code.service.id,
        costPerUnit: code.costPerUnit ? parseFloat(code.costPerUnit) : 0,
        numberOfUnits: code.numberOfUnits ? parseFloat(code.numberOfUnits) : 0,
        billingCodes: code.service.billingCodes.map(bc => ({
          ...bc,
          costPerUnit: code.costPerUnit ? parseFloat(code.costPerUnit) : 0,
          numberOfUnits: code.numberOfUnits
            ? parseFloat(code.numberOfUnits)
            : 0,
          scheduleEventId: this.scheduleEventId,
          serviceId: code.service.id
        }))
      }));
      let services = this.services.map(service => ({
        serviceId: service.value,
        costPerUnit:
          service.billingCodes && service.billingCodes[0].costPerUnit
            ? parseFloat(service.billingCodes[0].costPerUnit)
            : 0,
        numberOfUnits:
          service.billingCodes && service.billingCodes[0].numberOfUnits
            ? parseFloat(service.billingCodes[0].numberOfUnits)
            : 0,

        billingCodes: service.billingCodes.map(bc => ({
          ...bc,
          costPerUnit:
            service.billingCodes && service.billingCodes[0].costPerUnit
              ? parseFloat(service.billingCodes[0].costPerUnit)
              : 0,
          numberOfUnits:
            service.billingCodes && service.billingCodes[0].numberOfUnits
              ? parseFloat(service.billingCodes[0].numberOfUnits)
              : 0,
          scheduleEventId: this.scheduleEventId,
          serviceId: service.value
        }))
      }));
      if (!this.validateServices(services)) {
        return;
      } else if (!this.validateServices()) {
        await this.showError({
          message:
            "Failed to add CPT Codes, all CPT Codes added previously must have positive values."
        }).then(() => {
          this.$refs["form-modal"].hide();
        });
        return;
      }
      services = services.concat(currentServices);

      await scheduleEventServer
        .updateServices(this.scheduleEventId, services)
        .then(() => {
          this.$refs["form-modal"].hide();
          this.getCptCodes();
          this.showAlertSuccess();
        })
        .catch(() => this.showError());
    },
    async updateServices() {
      if (!this.validateServices()) {
        return;
      }
      const services = this.cptCodes.map(code => ({
        serviceId: code.service.id,
        costPerUnit: code.costPerUnit ? parseFloat(code.costPerUnit) : 0,
        numberOfUnits: code.numberOfUnits ? parseFloat(code.numberOfUnits) : 0,
        billingCodes: code.service.billingCodes.map(bc => ({
          ...bc,
          costPerUnit: code.costPerUnit ? parseFloat(code.costPerUnit) : 0,
          numberOfUnits: code.numberOfUnits
            ? parseFloat(code.numberOfUnits)
            : 0,
          scheduleEventId: this.scheduleEventId,
          serviceId: code.service.id
        }))
      }));
      await scheduleEventServer
        .updateServices(this.scheduleEventId, services)
        .then(() => {
          this.$refs["form-modal"].hide();
          this.getCptCodes();
          this.showAlertSuccess();
        })
        .catch(() => {
          this.getCptCodes();
          this.showError();
        });
    }
  },
  computed: {
    ...mapGetters("layout", ["hasCurrentUserPermissionAccess"]),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    disableControls() {
      return (
        this.encounterSubmited &&
        !this.hasCurrentUserPermissionAccess("others", "editChargedEvents")
      );
    }
  },
  watch: {
    services: {
      handler: async function(newValue) {
        if (
          newValue.length > 0 &&
          this.scheduleEvent.startDateTime != null &&
          this.scheduleEvent.endDateTime != null
        ) {
          let serviceIds = [];
          for (let service of newValue) {
            serviceIds.push(service.value);
          }
          let status =
            this.scheduleEvent.type == "groupSession"
              ? "Show"
              : this.scheduleEvent.status.name;
          let servicePerUnit = await scheduleEventServer.calculateNumberOfUnitByApptDuration(
            this.scheduleEvent.startDateTime,
            this.scheduleEvent.endDateTime,
            status,
            serviceIds
          );

          if (Object.keys(servicePerUnit).length > 0) {
            newValue.forEach(service => {
              service.billingCodes.forEach(bc => {
                bc.numberOfUnits = servicePerUnit[service.value];
              });
            });
          }
        }
      }
    }
  }
};
</script>

<style scoped>
.document-cpt-codes-tab-container {
  position: relative;
  flex-grow: 1;
  width: 100%;
  height: 100%;
}
.cpt-code-title {
  font-size: 1.15rem;
  font-weight: 700;
}
.cpt-code-badge {
  max-height: 30px;
  padding-top: 2px;
  padding-bottom: 2px;
}
.cpt-code-description {
  font-size: 0.95rem;
}
.cpt-code-content {
  font-size: 0.9rem;
}
</style>
